import Datepicker                                       from "./Datepicker"
import { useState, useEffect, useCallback, useMemo }    from "react"
import dayjs                                            from "dayjs"

const _datepickerViewSelector = {

    "DD/MM/YY": [ "year", "month", "day" ],
    "DD/MM/YYYY": [ "year", "month", "day" ],
    "MM/YY":    [ "year", "month" ],
    "YYYY":     [ "year" ]                   
    
}

const DatepickerContainer = ({

    selectedValue,
    onChange,
    datesList,
    format,
    disabled = false

}) => {

    const [ _dateList,          _setDateList            ] = useState([])
    const [ _datepickerViews,   _setDatepickerViews     ] = useState(['year'])  
    
    const _dateSet = useMemo(() => new Set(_dateList), [_dateList]);
    
    const _findFirstAllowedMonth = (yy) => {
        //this function is used to find the first month of the year that is available
        const fullYear = Number(`20${yy}`);

        const months = (datesList || [])
            .map((d) => dayjs(d, "YYYY-MM-DD", true))
            .filter((p) => p.isValid() && p.year() === fullYear)
            .map((p) => p.month() + 1) // month() é 0-11
            .sort((a, b) => a - b);

        return months.length ? months[0] : null;

    };

    const _handleYearChange = (newYear) => {
        
        const yy = newYear.format("YY");
        const firstAllowed = _findFirstAllowedMonth(yy);

        if (!firstAllowed) return;

        const next = newYear.month(firstAllowed - 1).date(1);
        _handleDateChange(next);

    }

    const _availableOptions = useCallback(() => {
        
        switch (format) {
            
            case "MM/YY":
                return {
                    shouldDisableMonth: (date) => !_dateSet.has(date.format("MM/YY")),
                    shouldDisableYear:  (date) => !_dateList.some((m) => m.endsWith(`/${date.format("YY")}`)),
                };

            case "DD/MM/YY":
            case "DD/MM/YYYY":  return { shouldDisableDate: (date) => !_dateSet.has(date.format(format)) };
            case "YYYY":        return { shouldDisableYear: (date) => !_dateSet.has(date.format("YYYY")) };
            default:            return {};
        }
    }, [ format, _dateList, _dateSet ]);

    const _handleError = (reason, value) => {
        
        console.log("❌ DatePicker error reason:", reason);
        console.log("❌ DatePicker value:", value?.format?.());
        console.log("❌ DatePicker value:", value);
    
    }

    const _handleDateAdapter = useCallback((value) => {
        
        return dayjs(value, "YYYY-MM-DD", true).format(format)

    }, [ format ])

    useEffect(() => {

        const datepickerViews  = _datepickerViewSelector[format]
        _setDatepickerViews(datepickerViews)

    }, [ format ])

    useEffect(() => {
        
        const adaptedDatesList      = (datesList || []).map(_handleDateAdapter)
        const deduplicatedDatesList = Array.from(new Set(adaptedDatesList))

        _setDateList(deduplicatedDatesList)

    }, [ datesList, _handleDateAdapter ])

    const adaptedSelectedValue = selectedValue ? dayjs(selectedValue, "YYYY-MM-DD", true) : null

    const _handleDateChange = useCallback((value) => {
        if (!value || !value.isValid()) {
            onChange(null);
            return;
        }

        const selectedMonth = value.format("MM/YY");
        const selectedYear = value.format("YYYY");
        const availableDate = (datesList || []).find((date) => {
            const parsedDate = dayjs(date, "YYYY-MM-DD", true);

            if (!parsedDate.isValid()) return false;
            if (format === "MM/YY") return parsedDate.format("MM/YY") === selectedMonth;
            if (format === "YYYY") return parsedDate.format("YYYY") === selectedYear;

            return parsedDate.format("YYYY-MM-DD") === value.format("YYYY-MM-DD");
        });

        onChange(availableDate || null);
    }, [ datesList, format, onChange ])

    return (
        <>

            <Datepicker 
                
                datesList={_dateList} 
                handleError={_handleError} 
                
                selectedValue={adaptedSelectedValue}
                onChange={_handleDateChange}

                views={_datepickerViews}
                format={format}
                
                {..._availableOptions()}

                onYearChange={(newValue) => _handleYearChange(newValue)}
                disabled={disabled}
                
            />
            
        </>
    )
}

export default DatepickerContainer
